import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ATTENDANCE_STATUSES,
  ATTENDANCE_STATUS_MAP,
  type AttendanceStatus,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { Badge, EmptyState, ErrorState, PageHead, Stat, TableSkeleton } from '../../components/ui';

interface SheetRow {
  employeeId: string;
  name: string;
  code: string | null;
  jobTitle: string | null;
  department: string | null;
  recorded: boolean;
  status: AttendanceStatus;
  checkIn: string | null;
  checkOut: string | null;
  lateMinutes: number;
  overtimeMinutes: number;
  notes: string | null;
  onApprovedLeave: string | null;
}

interface Sheet {
  date: string;
  isWeekend: boolean;
  holiday: { name: string } | null;
  rows: SheetRow[];
}

const today = () => new Date().toISOString().slice(0, 10);

/**
 * كشف الحضور اليومي.
 *
 * يوم واحد للفريق كله، يُملأ ويُحفظ مرة واحدة. الحفظ صفًا صفًا كان يترك
 * اليوم نصف مسجَّل إن انقطع الاتصال في المنتصف، ويُحوّل عملًا من دقيقتين
 * إلى عشرين ضغطة.
 */
export function AttendanceSheet() {
  const queryClient = useQueryClient();
  const [day, setDay] = useState(today());
  const [draft, setDraft] = useState<Record<string, Partial<SheetRow>>>({});

  const sheet = useQuery({
    queryKey: ['attendance', 'sheet', day],
    queryFn: () => api<Sheet>(`/hr/attendance/sheet?date=${day}`),
  });

  /*
   * المسودة تُمسح عند تغيير اليوم.
   *
   * إبقاؤها كان ينقل تعديلات يوم إلى يوم آخر بلا أن يلاحظ أحد — والصفوف
   * مفاتيحها معرّفات موظفين، وهي نفسها في كل يوم.
   */
  useEffect(() => {
    setDraft({});
  }, [day]);

  const save = useMutation({
    mutationFn: () => {
      const rows = (sheet.data?.rows ?? []).map((row) => {
        const edit = draft[row.employeeId] ?? {};
        const status = (edit.status ?? row.status) as AttendanceStatus;
        return {
          employeeId: row.employeeId,
          // المعلّق ليس حالة تُحفظ: يُسجَّل حاضرًا ما لم يُقل غير ذلك.
          status: status === 'PENDING' ? 'PRESENT' : status,
          checkIn: edit.checkIn !== undefined ? edit.checkIn : row.checkIn,
          checkOut: edit.checkOut !== undefined ? edit.checkOut : row.checkOut,
          lateMinutes: edit.lateMinutes ?? row.lateMinutes,
          overtimeMinutes: edit.overtimeMinutes ?? row.overtimeMinutes,
          notes: edit.notes !== undefined ? edit.notes : row.notes,
        };
      });
      return api<{ saved: number }>('/hr/attendance', { method: 'POST', body: { date: day, rows } });
    },
    onSuccess: () => {
      setDraft({});
      void queryClient.invalidateQueries({ queryKey: ['attendance'] });
    },
  });

  const edit = (employeeId: string, patch: Partial<SheetRow>) =>
    setDraft((d) => ({ ...d, [employeeId]: { ...d[employeeId], ...patch } }));

  const valueOf = <K extends keyof SheetRow>(row: SheetRow, key: K): SheetRow[K] => {
    const edited = draft[row.employeeId]?.[key];
    return (edited !== undefined ? edited : row[key]) as SheetRow[K];
  };

  const rows = sheet.data?.rows ?? [];
  const dirty = Object.keys(draft).length > 0;
  const unrecorded = rows.filter((r) => !r.recorded).length;
  const closed = sheet.data?.isWeekend || Boolean(sheet.data?.holiday);

  return (
    <div className="page">
      <PageHead
        title="الحضور"
        subtitle={date(day)}
        actions={
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={save.isPending || rows.length === 0}
          >
            {save.isPending ? 'جارٍ الحفظ' : dirty ? 'حفظ التعديلات' : 'حفظ الكشف'}
          </button>
        }
      />

      <div className="toolbar">
        <input
          className="input num"
          type="date"
          value={day}
          max={today()}
          onChange={(e) => setDay(e.target.value)}
        />
        <button className="btn" onClick={() => setDay(shift(day, -1))}>
          اليوم السابق
        </button>
        <button className="btn" onClick={() => setDay(today())} disabled={day === today()}>
          اليوم
        </button>
        <button className="btn" onClick={() => setDay(shift(day, 1))} disabled={day >= today()}>
          اليوم التالي
        </button>
      </div>

      {sheet.data?.holiday && (
        <div className="alert alert-info mb-4">
          هذا اليوم عطلة رسمية: {sheet.data.holiday.name}. لا يُحتسب غيابًا ولا يُخصم من المرتب.
        </div>
      )}
      {sheet.data?.isWeekend && !sheet.data.holiday && (
        <div className="alert alert-info mb-4">
          هذا اليوم عطلة أسبوعية حسب إعدادات الشركة.
        </div>
      )}

      <div className="stats">
        <Stat label="الفريق" value={rows.length} />
        <Stat label="لم يُسجَّل بعد" value={unrecorded} />
        <Stat
          label="حاضرون"
          value={rows.filter((r) => ['PRESENT', 'LATE'].includes(valueOf(r, 'status'))).length}
        />
      </div>

      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}
      {save.isSuccess && !dirty && (
        <div className="alert alert-info mb-4">حُفظ كشف {date(day)}.</div>
      )}

      {sheet.isError && <ErrorState error={sheet.error} onRetry={() => sheet.refetch()} />}

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>الموظف</th>
                <th>الحالة</th>
                <th>الحضور</th>
                <th>الانصراف</th>
                <th>إضافي (دقيقة)</th>
                <th>ملاحظة</th>
              </tr>
            </thead>
            {sheet.isLoading && <TableSkeleton rows={6} cols={6} />}

            <tbody>
              {rows.map((row) => {
                const status = valueOf(row, 'status');
                return (
                  <tr key={row.employeeId}>
                    <td>
                      <span className="cell-strong">{row.name}</span>
                      <div className="tiny mute">
                        {row.jobTitle}
                        {row.onApprovedLeave && (
                          <>
                            {row.jobTitle && ' — '}
                            <span className="pos">له إجازة معتمدة اليوم</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <select
                        className="select select-sm"
                        value={status}
                        onChange={(e) =>
                          edit(row.employeeId, { status: e.target.value as AttendanceStatus })
                        }
                      >
                        {ATTENDANCE_STATUSES.filter((s) => s !== 'PENDING' || status === 'PENDING').map(
                          (s) => (
                            <option key={s} value={s}>
                              {ATTENDANCE_STATUS_MAP[s].label}
                            </option>
                          ),
                        )}
                      </select>
                    </td>
                    <td>
                      <input
                        className="input input-sm num"
                        type="time"
                        value={valueOf(row, 'checkIn') ?? ''}
                        onChange={(e) =>
                          edit(row.employeeId, { checkIn: e.target.value || null })
                        }
                        disabled={!ATTENDANCE_STATUS_MAP[status].workday}
                      />
                    </td>
                    <td>
                      <input
                        className="input input-sm num"
                        type="time"
                        value={valueOf(row, 'checkOut') ?? ''}
                        onChange={(e) =>
                          edit(row.employeeId, { checkOut: e.target.value || null })
                        }
                        disabled={!ATTENDANCE_STATUS_MAP[status].workday}
                      />
                    </td>
                    <td>
                      <input
                        className="input input-sm num"
                        dir="ltr"
                        inputMode="numeric"
                        value={valueOf(row, 'overtimeMinutes')}
                        onChange={(e) =>
                          edit(row.employeeId, {
                            overtimeMinutes: Number(e.target.value) || 0,
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        className="input input-sm"
                        value={valueOf(row, 'notes') ?? ''}
                        onChange={(e) => edit(row.employeeId, { notes: e.target.value || null })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!sheet.isLoading && rows.length === 0 && (
          <EmptyState
            title="لا موظفين على رأس العمل"
            text="أضف ملفات الموظفين أولًا ليظهروا في كشف الحضور."
          />
        )}
      </div>

      <p className="tiny mute mt-3">
        التأخير يُحسب تلقائيًا من موعد بدء الدوام عند اختيار "متأخر" — يُضبط الموعد ومهلته من
        إعدادات الشركة. والخصم على التأخير لا يقع تلقائيًا: أضفه بندًا صريحًا في المرتبات.
      </p>
    </div>
  );
}

function shift(day: string, days: number): string {
  const d = new Date(`${day}T00:00:00.000Z`);
  return new Date(d.getTime() + days * 86_400_000).toISOString().slice(0, 10);
}
